// SolarInstallationCertifications.js - Solar installation themed Certifications & Awards section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a solar installation themed Certifications & Awards section with credential cards
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the certifications section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The certifications section elements
 */
export const createSolarInstallationCertificationsElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const certificationsSectionClass = 'solar-certifications-section';
  const certificationsContainerClass = 'solar-certifications-container';
  const certificationsHeaderClass = 'solar-certifications-header';
  const certificationsTitleClass = 'solar-certifications-title';
  const certificationsSubtitleClass = 'solar-certifications-subtitle';
  const certificationsGridClass = 'solar-certifications-grid';
  const certificationCardClass = 'solar-certification-card';
  const certificationIconClass = 'solar-certification-icon';
  const certificationNameClass = 'solar-certification-name';
  const certificationDescClass = 'solar-certification-description';
  const certificationYearClass = 'solar-certification-year';
  const certificationBadgeClass = 'solar-certification-badge';
  const certificationsPatternOverlayClass = 'solar-certifications-pattern-overlay';

  // Desktop Styles - Solar themed with light background and green accents
  const certificationsSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f8fafc',
    background: `
      radial-gradient(circle at 20% 30%, rgba(74, 222, 128, 0.12) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(34, 197, 94, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 50% 50%, rgba(15, 35, 65, 0.05) 0%, transparent 70%),
      linear-gradient(135deg, #f8fafc 0%, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%, #f8fafc 100%)
    `,
    color: '#1e293b',
    width: '100%',
    overflow: 'hidden'
  };

  const certificationsPatternOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: `
      radial-gradient(circle at 20% 30%, rgba(34, 197, 94, 0.03) 1px, transparent 1px),
      radial-gradient(circle at 80% 70%, rgba(34, 197, 94, 0.03) 1px, transparent 1px)
    `,
    backgroundSize: '80px 80px, 120px 120px',
    backgroundPosition: '0 0, 60px 60px',
    zIndex: '0',
    opacity: '0.5'
  };

  const certificationsContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: '1'
  };

  const certificationsHeaderStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const certificationsTitleStyles = {
    fontSize: '48px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif',
    letterSpacing: '-0.02em',
    lineHeight: '1.2'
  };

  const certificationsSubtitleStyles = {
    fontSize: '20px',
    color: '#475569',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  const certificationsGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '30px',
    marginTop: '60px'
  };

  const certificationCardStyles = {
    background: '#ffffff',
    border: '1px solid rgba(34, 197, 94, 0.2)',
    borderRadius: '12px',
    padding: '40px 30px',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  };

  const certificationCardHoverStyles = {
    transform: 'translateY(-4px)',
    borderColor: '#22c55e',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 20px rgba(34, 197, 94, 0.2)'
  };

  const certificationIconStyles = {
    width: '80px',
    height: '80px',
    margin: '0 auto 25px auto',
    display: 'block',
    borderRadius: '50%',
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '36px',
    color: '#22c55e',
    transition: 'all 0.3s ease'
  };

  const certificationNameStyles = {
    fontSize: '22px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '12px',
    fontFamily: '"Inter", sans-serif',
    lineHeight: '1.3'
  };

  const certificationDescStyles = {
    fontSize: '16px',
    color: '#475569',
    lineHeight: '1.6',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  const certificationYearStyles = {
    fontSize: '14px',
    fontWeight: '500',
    color: '#64748b',
    marginBottom: '15px',
    fontFamily: '"Inter", sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  };

  const certificationBadgeStyles = {
    display: 'inline-block',
    padding: '6px 12px',
    backgroundColor: '#22c55e',
    color: '#ffffff',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    fontFamily: '"Inter", sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  };

  // Tablet Styles
  const tabletCertificationsGridStyles = {
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '25px'
  };

  const tabletCertificationCardStyles = {
    padding: '35px 25px'
  };

  // Mobile Styles
  const mobileCertificationsTitleStyles = {
    fontSize: '36px'
  };

  const mobileCertificationsGridStyles = {
    gridTemplateColumns: '1fr',
    gap: '20px'
  };

  const mobileCertificationCardStyles = {
    padding: '30px 20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(certificationsSectionClass, certificationsSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationsPatternOverlayClass, certificationsPatternOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationsContainerClass, certificationsContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationsHeaderClass, certificationsHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationsTitleClass, certificationsTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationsSubtitleClass, certificationsSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationsGridClass, certificationsGridStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationCardClass, certificationCardStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationIconClass, certificationIconStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationNameClass, certificationNameStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationDescClass, certificationDescStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationYearClass, certificationYearStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationBadgeClass, certificationBadgeStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${certificationCardClass}--hover`, certificationCardHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(certificationsGridClass, tabletCertificationsGridStyles);
    newRegistry[DEVICES.TABLET].set(certificationCardClass, tabletCertificationCardStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(certificationsTitleClass, mobileCertificationsTitleStyles);
    newRegistry[DEVICES.MOBILE].set(certificationsGridClass, mobileCertificationsGridStyles);
    newRegistry[DEVICES.MOBILE].set(certificationCardClass, mobileCertificationCardStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    certificationsSectionClass,
    certificationsPatternOverlayClass,
    certificationsContainerClass,
    certificationsHeaderClass,
    certificationsTitleClass,
    certificationsSubtitleClass,
    certificationsGridClass,
    certificationCardClass,
    certificationIconClass,
    certificationNameClass,
    certificationDescClass,
    certificationYearClass,
    certificationBadgeClass
  ]));

  // Generate IDs for elements
  const sectionId = Date.now();
  const patternOverlayId = Date.now() + 1;
  const containerId = Date.now() + 2;
  const headerId = Date.now() + 3;
  const titleId = Date.now() + 4;
  const subtitleId = Date.now() + 5;
  const gridId = Date.now() + 6;
  
  // Certification card IDs
  const cert1Id = Date.now() + 10;
  const cert1IconId = Date.now() + 11;
  const cert1NameId = Date.now() + 12;
  const cert1DescId = Date.now() + 13;
  const cert1YearId = Date.now() + 14;
  const cert1BadgeId = Date.now() + 15;
  
  const cert2Id = Date.now() + 20;
  const cert2IconId = Date.now() + 21;
  const cert2NameId = Date.now() + 22;
  const cert2DescId = Date.now() + 23;
  const cert2YearId = Date.now() + 24;
  const cert2BadgeId = Date.now() + 25;
  
  const cert3Id = Date.now() + 30;
  const cert3IconId = Date.now() + 31;
  const cert3NameId = Date.now() + 32;
  const cert3DescId = Date.now() + 33;
  const cert3YearId = Date.now() + 34;
  const cert3BadgeId = Date.now() + 35;
  
  const cert4Id = Date.now() + 40;
  const cert4IconId = Date.now() + 41;
  const cert4NameId = Date.now() + 42;
  const cert4DescId = Date.now() + 43;
  const cert4YearId = Date.now() + 44;
  const cert4BadgeId = Date.now() + 45;
  
  const cert5Id = Date.now() + 50;
  const cert5IconId = Date.now() + 51;
  const cert5NameId = Date.now() + 52;
  const cert5DescId = Date.now() + 53;
  const cert5YearId = Date.now() + 54;
  const cert5BadgeId = Date.now() + 55;
  
  const cert6Id = Date.now() + 60;
  const cert6IconId = Date.now() + 61;
  const cert6NameId = Date.now() + 62;
  const cert6DescId = Date.now() + 63;
  const cert6YearId = Date.now() + 64;
  const cert6BadgeId = Date.now() + 65;

  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Certifications Section',
      parentId: null,
      className: certificationsSectionClass,
      styles: {},
      children: [patternOverlayId, containerId],
      text: '',
      attributes: {
        'data-element': 'certifications-section',
        'data-component': 'section',
        'data-section-type': 'certifications',
        'data-page-id': currentPageId
      }
    },
    
    // Pattern overlay
    {
      id: patternOverlayId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pattern Overlay',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: certificationsPatternOverlayClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'certifications-pattern-overlay'
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certifications Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: certificationsContainerClass,
      styles: {},
      children: [headerId, gridId],
      text: '',
      attributes: {
        'data-element': 'certifications-container'
      }
    },
    
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certifications Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: certificationsHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'certifications-header'
      }
    },
    
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Certifications Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: certificationsTitleClass,
      styles: {},
      children: [],
      text: 'Certifications & Awards',
      attributes: {
        'data-element': 'certifications-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Certifications Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: certificationsSubtitleClass,
      styles: {},
      children: [],
      text: 'Our industry credentials and recognitions demonstrate our commitment to excellence, safety, and professional standards in solar installation and electrical services.',
      attributes: {
        'data-element': 'certifications-subtitle'
      }
    },
    
    // Grid
    {
      id: gridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certifications Grid',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: certificationsGridClass,
      styles: {},
      children: [cert1Id, cert2Id, cert3Id, cert4Id, cert5Id, cert6Id],
      text: '',
      attributes: {
        'data-element': 'certifications-grid'
      }
    },
    
    // Certification 1 - NABCEP Certified
    {
      id: cert1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 1',
      parentId: gridId,
      hierarchyName: getHierarchyName('div'),
      className: certificationCardClass,
      styles: {},
      children: [cert1IconId, cert1NameId, cert1DescId, cert1YearId, cert1BadgeId],
      text: '',
      attributes: {
        'data-element': 'certification-card'
      }
    },
    
    {
      id: cert1IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 1 Icon',
      parentId: cert1Id,
      hierarchyName: getHierarchyName('div'),
      className: certificationIconClass,
      styles: {},
      children: [],
      text: '🏆',
      attributes: {
        'data-element': 'certification-icon'
      }
    },
    
    {
      id: cert1NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Certification 1 Name',
      parentId: cert1Id,
      hierarchyName: getHierarchyName('h3'),
      className: certificationNameClass,
      styles: {},
      children: [],
      text: 'NABCEP Certified',
      attributes: {
        'data-element': 'certification-name'
      }
    },
    
    {
      id: cert1DescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Certification 1 Description',
      parentId: cert1Id,
      hierarchyName: getHierarchyName('p'),
      className: certificationDescClass,
      styles: {},
      children: [],
      text: 'North American Board of Certified Energy Practitioners certification for solar PV installation professionals.',
      attributes: {
        'data-element': 'certification-description'
      }
    },
    
    {
      id: cert1YearId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 1 Year',
      parentId: cert1Id,
      hierarchyName: getHierarchyName('div'),
      className: certificationYearClass,
      styles: {},
      children: [],
      text: 'Certified Since 2018',
      attributes: {
        'data-element': 'certification-year'
      }
    },
    
    {
      id: cert1BadgeId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Certification 1 Badge',
      parentId: cert1Id,
      hierarchyName: getHierarchyName('span'),
      className: certificationBadgeClass,
      styles: {},
      children: [],
      text: 'Active',
      attributes: {
        'data-element': 'certification-badge'
      }
    },
    
    // Certification 2 - OSHA Safety
    {
      id: cert2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 2',
      parentId: gridId,
      hierarchyName: getHierarchyName('div'),
      className: certificationCardClass,
      styles: {},
      children: [cert2IconId, cert2NameId, cert2DescId, cert2YearId, cert2BadgeId],
      text: '',
      attributes: {
        'data-element': 'certification-card'
      }
    },
    
    {
      id: cert2IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 2 Icon',
      parentId: cert2Id,
      hierarchyName: getHierarchyName('div'),
      className: certificationIconClass,
      styles: {},
      children: [],
      text: '🛡️',
      attributes: {
        'data-element': 'certification-icon'
      }
    },
    
    {
      id: cert2NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Certification 2 Name',
      parentId: cert2Id,
      hierarchyName: getHierarchyName('h3'),
      className: certificationNameClass,
      styles: {},
      children: [],
      text: 'OSHA Safety Certified',
      attributes: {
        'data-element': 'certification-name'
      }
    },
    
    {
      id: cert2DescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Certification 2 Description',
      parentId: cert2Id,
      hierarchyName: getHierarchyName('p'),
      className: certificationDescClass,
      styles: {},
      children: [],
      text: 'Occupational Safety and Health Administration certification ensuring workplace safety standards compliance.',
      attributes: {
        'data-element': 'certification-description'
      }
    },
    
    {
      id: cert2YearId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 2 Year',
      parentId: cert2Id,
      hierarchyName: getHierarchyName('div'),
      className: certificationYearClass,
      styles: {},
      children: [],
      text: 'Renewed 2024',
      attributes: {
        'data-element': 'certification-year'
      }
    },
    
    {
      id: cert2BadgeId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Certification 2 Badge',
      parentId: cert2Id,
      hierarchyName: getHierarchyName('span'),
      className: certificationBadgeClass,
      styles: {},
      children: [],
      text: 'Current',
      attributes: {
        'data-element': 'certification-badge'
      }
    },
    
    // Certification 3 - Licensed Electrician
    {
      id: cert3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 3',
      parentId: gridId,
      hierarchyName: getHierarchyName('div'),
      className: certificationCardClass,
      styles: {},
      children: [cert3IconId, cert3NameId, cert3DescId, cert3YearId, cert3BadgeId],
      text: '',
      attributes: {
        'data-element': 'certification-card'
      }
    },
    
    {
      id: cert3IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 3 Icon',
      parentId: cert3Id,
      hierarchyName: getHierarchyName('div'),
      className: certificationIconClass,
      styles: {},
      children: [],
      text: '⚡',
      attributes: {
        'data-element': 'certification-icon'
      }
    },
    
    {
      id: cert3NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Certification 3 Name',
      parentId: cert3Id,
      hierarchyName: getHierarchyName('h3'),
      className: certificationNameClass,
      styles: {},
      children: [],
      text: 'Licensed Electrician',
      attributes: {
        'data-element': 'certification-name'
      }
    },
    
    {
      id: cert3DescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Certification 3 Description',
      parentId: cert3Id,
      hierarchyName: getHierarchyName('p'),
      className: certificationDescClass,
      styles: {},
      children: [],
      text: 'State licensed master electrician qualified for all electrical installations and repairs.',
      attributes: {
        'data-element': 'certification-description'
      }
    },
    
    {
      id: cert3YearId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 3 Year',
      parentId: cert3Id,
      hierarchyName: getHierarchyName('div'),
      className: certificationYearClass,
      styles: {},
      children: [],
      text: 'Licensed Since 2015',
      attributes: {
        'data-element': 'certification-year'
      }
    },
    
    {
      id: cert3BadgeId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Certification 3 Badge',
      parentId: cert3Id,
      hierarchyName: getHierarchyName('span'),
      className: certificationBadgeClass,
      styles: {},
      children: [],
      text: 'Master',
      attributes: {
        'data-element': 'certification-badge'
      }
    },
    
    // Certification 4 - Solar Excellence Award
    {
      id: cert4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 4',
      parentId: gridId,
      hierarchyName: getHierarchyName('div'),
      className: certificationCardClass,
      styles: {},
      children: [cert4IconId, cert4NameId, cert4DescId, cert4YearId, cert4BadgeId],
      text: '',
      attributes: {
        'data-element': 'certification-card'
      }
    },
    
    {
      id: cert4IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 4 Icon',
      parentId: cert4Id,
      hierarchyName: getHierarchyName('div'),
      className: certificationIconClass,
      styles: {},
      children: [],
      text: '🥇',
      attributes: {
        'data-element': 'certification-icon'
      }
    },
    
    {
      id: cert4NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Certification 4 Name',
      parentId: cert4Id,
      hierarchyName: getHierarchyName('h3'),
      className: certificationNameClass,
      styles: {},
      children: [],
      text: 'Solar Excellence Award',
      attributes: {
        'data-element': 'certification-name'
      }
    },
    
    {
      id: cert4DescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Certification 4 Description',
      parentId: cert4Id,
      hierarchyName: getHierarchyName('p'),
      className: certificationDescClass,
      styles: {},
      children: [],
      text: 'Regional Solar Industry Association award for outstanding installation quality and customer satisfaction.',
      attributes: {
        'data-element': 'certification-description'
      }
    },
    
    {
      id: cert4YearId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 4 Year',
      parentId: cert4Id,
      hierarchyName: getHierarchyName('div'),
      className: certificationYearClass,
      styles: {},
      children: [],
      text: 'Awarded 2023',
      attributes: {
        'data-element': 'certification-year'
      }
    },
    
    {
      id: cert4BadgeId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Certification 4 Badge',
      parentId: cert4Id,
      hierarchyName: getHierarchyName('span'),
      className: certificationBadgeClass,
      styles: {},
      children: [],
      text: 'Award',
      attributes: {
        'data-element': 'certification-badge'
      }
    },
    
    // Certification 5 - Green Energy Certified
    {
      id: cert5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 5',
      parentId: gridId,
      hierarchyName: getHierarchyName('div'),
      className: certificationCardClass,
      styles: {},
      children: [cert5IconId, cert5NameId, cert5DescId, cert5YearId, cert5BadgeId],
      text: '',
      attributes: {
        'data-element': 'certification-card'
      }
    },
    
    {
      id: cert5IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 5 Icon',
      parentId: cert5Id,
      hierarchyName: getHierarchyName('div'),
      className: certificationIconClass,
      styles: {},
      children: [],
      text: '🌱',
      attributes: {
        'data-element': 'certification-icon'
      }
    },
    
    {
      id: cert5NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Certification 5 Name',
      parentId: cert5Id,
      hierarchyName: getHierarchyName('h3'),
      className: certificationNameClass,
      styles: {},
      children: [],
      text: 'Green Energy Certified',
      attributes: {
        'data-element': 'certification-name'
      }
    },
    
    {
      id: cert5DescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Certification 5 Description',
      parentId: cert5Id,
      hierarchyName: getHierarchyName('p'),
      className: certificationDescClass,
      styles: {},
      children: [],
      text: 'Certified green energy specialist with expertise in sustainable energy solutions and environmental compliance.',
      attributes: {
        'data-element': 'certification-description'
      }
    },
    
    {
      id: cert5YearId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 5 Year',
      parentId: cert5Id,
      hierarchyName: getHierarchyName('div'),
      className: certificationYearClass,
      styles: {},
      children: [],
      text: 'Certified 2020',
      attributes: {
        'data-element': 'certification-year'
      }
    },
    
    {
      id: cert5BadgeId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Certification 5 Badge',
      parentId: cert5Id,
      hierarchyName: getHierarchyName('span'),
      className: certificationBadgeClass,
      styles: {},
      children: [],
      text: 'Eco-Certified',
      attributes: {
        'data-element': 'certification-badge'
      }
    },
    
    // Certification 6 - Security Systems Certified
    {
      id: cert6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 6',
      parentId: gridId,
      hierarchyName: getHierarchyName('div'),
      className: certificationCardClass,
      styles: {},
      children: [cert6IconId, cert6NameId, cert6DescId, cert6YearId, cert6BadgeId],
      text: '',
      attributes: {
        'data-element': 'certification-card'
      }
    },
    
    {
      id: cert6IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 6 Icon',
      parentId: cert6Id,
      hierarchyName: getHierarchyName('div'),
      className: certificationIconClass,
      styles: {},
      children: [],
      text: '🔒',
      attributes: {
        'data-element': 'certification-icon'
      }
    },
    
    {
      id: cert6NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Certification 6 Name',
      parentId: cert6Id,
      hierarchyName: getHierarchyName('h3'),
      className: certificationNameClass,
      styles: {},
      children: [],
      text: 'Security Systems Certified',
      attributes: {
        'data-element': 'certification-name'
      }
    },
    
    {
      id: cert6DescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Certification 6 Description',
      parentId: cert6Id,
      hierarchyName: getHierarchyName('p'),
      className: certificationDescClass,
      styles: {},
      children: [],
      text: 'Professional certification for CCTV, access control, and communication systems installation and maintenance.',
      attributes: {
        'data-element': 'certification-description'
      }
    },
    
    {
      id: cert6YearId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certification 6 Year',
      parentId: cert6Id,
      hierarchyName: getHierarchyName('div'),
      className: certificationYearClass,
      styles: {},
      children: [],
      text: 'Certified 2019',
      attributes: {
        'data-element': 'certification-year'
      }
    },
    
    {
      id: cert6BadgeId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Certification 6 Badge',
      parentId: cert6Id,
      hierarchyName: getHierarchyName('span'),
      className: certificationBadgeClass,
      styles: {},
      children: [],
      text: 'Professional',
      attributes: {
        'data-element': 'certification-badge'
      }
    }
  ];

  return {
    certificationsSection: newElements[0],
    elements: newElements
  };
} 